import {
  IonContent,
  IonInput,
  IonModal,
  IonIcon,
} from "@ionic/react";
import { Button, Input, message, Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { stringify } from "svgson";
import SVGtoPNGResizer from "./SVGtoPNGResizer";
import SvgExportSettings from "./SvgExportSettings";

import { CopyOutlined, CheckOutlined } from "@ant-design/icons";
import { closeOutline, documentOutline, imageOutline, shareSocialOutline, linkOutline, diamondOutline, downloadOutline, imagesOutline, informationCircleOutline } from "ionicons/icons";
import { isNative } from "../../../utils/nativeDownload";
import EmbroideryConverter from "./EmbroideryConverter";
import { updateFilePublicStatus, generateShareLink, getFileMetadata, getSvgMetadataIdByPath } from "../../../lib/firebase/firebase";
import { trackIntercomEvent } from "../../../lib/intercom/intercom";
import { useSelector, useDispatch } from "react-redux";
import { useCopyToClipboard } from "usehooks-ts";
import { updateFilePrivacyInRedux as updateMyFilePrivacy } from "../../../redux/slices/MyFiles";
import { updateFilePrivacyInRedux as updateUploadedFilePrivacy } from "../../../redux/slices/UploadedFiles";
import { useAuth } from "../../../contexts/AuthContext";
import { isNativeiOS } from "../../../lib/utils/platform";
import IOSPaywall from "../../PaywallModal/IOSPaywall";
import { isSvgTrace } from "../../../config/appVariant";

const ALL_FORMAT_TABS = [
  { key: "svg", label: "SVG", icon: documentOutline },
  { key: "png", label: "PNG", icon: imageOutline },
];

const ExportFile = ({ svgJoined, children, buttonType = "text", fileName, style: externalStyle, rasterCommit, onCommitStart, isTablet, defaultTab, variant = "primary", onEditorModeChange, ...props }) => {
  const currentFile = useSelector((state) => state.files.currentFile);
  const dispatch = useDispatch();
  const { userIsPremium } = useAuth();
  const [showIOSPaywall, setShowIOSPaywall] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(defaultTab || "svg");
  const [publicLink, setPublicLink] = useState("");
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [isLoadingLink, setIsLoadingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [, copy] = useCopyToClipboard();
  const [isHovered, setIsHovered] = useState(false);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportName, setExportName] = useState(() => {
    let modifiedFileName = fileName || "File Name";
    if (modifiedFileName.endsWith(".svg")) {
      modifiedFileName = modifiedFileName.replace(/\.svg$/, "");
    }
    return "SvgTrace-" + modifiedFileName;
  });

  const pngRef = useRef(null);
  const svgRef = useRef(null);
  const FORMAT_TABS = isNativeiOS()
    ? ALL_FORMAT_TABS.filter((t) => !t.hideOnIOS)
    : ALL_FORMAT_TABS;

  const requirePremium = (action) => {
    if (isNativeiOS() && !userIsPremium) {
      setShowIOSPaywall(true);
      return;
    }
    action();
  };

  useEffect(() => {
    if (selectedFormat !== "share") {
      setPublicLink("");
      setLinkCopied(false);
      setIsLoadingLink(false);
    }
  }, [selectedFormat]);

  useEffect(() => {
    const populateShareLink = async () => {
      if (isCreatingLink) return;
      
      if (selectedFormat === "share" && currentFile?.isPublic && currentFile?.uri && !publicLink) {
        setIsLoadingLink(true);
        
        try {
          let correctFileId = await getSvgMetadataIdByPath(currentFile.uri);
          if (!correctFileId) {
            correctFileId = currentFile?.svgMetadataId || currentFile?.id;
          }
          if (!correctFileId) {
            setIsLoadingLink(false);
            return;
          }
          
          let title = '';
          const maxRetries = 5;
          const retryDelay = 1500;
          
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            const metadata = await getFileMetadata(correctFileId);
            if (metadata) {
              title = metadata.seoTitle || metadata.prompt || '';
              if (title) break;
            }
            if (attempt < maxRetries - 1) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
          }
          
          const shareLink = generateShareLink(correctFileId, title || '');
          setPublicLink(shareLink);
        } catch (error) {
          console.error("Error in auto-populate:", error);
        } finally {
          setIsLoadingLink(false);
        }
      }
    };
    
    populateShareLink();
  }, [selectedFormat, currentFile?.isPublic, currentFile?.uri]);

  const handleCreate = async () => {
    if (!currentFile?.uri) {
      message.error("No file selected to share");
      return;
    }

    setIsCreatingLink(true);
    setLinkCopied(false);

    try {
      let correctFileId = null;
      
      if (!currentFile.isPublic) {
        const result = await updateFilePublicStatus(currentFile.uri, true);
        if (result?.svgMetadataDocId) {
          correctFileId = result.svgMetadataDocId;
        }
        dispatch(updateMyFilePrivacy({ id: currentFile.id, isPublic: true }));
        dispatch(updateUploadedFilePrivacy({ id: currentFile.id, isPublic: true }));
      } else {
        correctFileId = await getSvgMetadataIdByPath(currentFile.uri);
      }
      
      if (!correctFileId) {
        correctFileId = currentFile?.svgMetadataId || currentFile?.id;
      }
      
      if (!correctFileId) {
        message.error("Unable to identify file. Please try again.");
        setIsCreatingLink(false);
        return;
      }

      let title = '';
      const maxRetries = 8;
      const baseDelay = 1500;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const metadata = await getFileMetadata(correctFileId);
          if (metadata) {
            title = metadata.seoTitle || metadata.prompt || '';
            if (title) {
              const shareLink = generateShareLink(correctFileId, title);
              setPublicLink(shareLink);
              message.success("Share link created! Your design is now public.");
              return;
            }
          }
          if (attempt < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, baseDelay));
          }
        } catch (fetchError) {
          console.error(`Attempt ${attempt + 1}: Error fetching metadata:`, fetchError);
        }
      }
      
      const fallbackLink = generateShareLink(correctFileId, '');
      setPublicLink(fallbackLink);
      message.success("Share link created! Your design is now public.");
    } catch (error) {
      console.error("Error creating share link:", error);
      message.error("Failed to create share link. Please try again.");
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await copy(publicLink);
      setLinkCopied(true);
      message.success("Link copied to clipboard!", 2);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      console.error(error);
      message.error("Failed to copy link. Please try again.", 2);
    }
  };

  const handleCopyCode = async () => {
    try {
      const svgCode = stringify(svgJoined);
      await copy(svgCode);
      message.success("SVG code copied to clipboard!", 2);
    } catch (error) {
      console.error(error);
      message.error("Failed to copy SVG code. Please try again.", 2);
    }
  };

  const handleOpenModal = () => {
    if (rasterCommit?.hasChanges && rasterCommit?.commit) {
      onCommitStart?.();
      rasterCommit.commit();
    }

    setSelectedFormat(defaultTab || "svg");
    setShowModal(true);
    
    if (window.tutorialTriggerAction) {
      window.tutorialTriggerAction('export-modal-opened', {
        component: 'ExportFile',
        timestamp: Date.now()
      });
    }
  };

  const handleExportSvg = () => {
    requirePremium(async () => {
      setIsExporting(true);
      trackIntercomEvent('exported_file', { format: 'svg' });
      try {
        await svgRef.current?.export();
      } finally {
        setIsExporting(false);
      }
    });
  };

  const handleExportPng = () => {
    requirePremium(async () => {
      setIsExporting(true);
      try {
        await pngRef.current?.export();
      } finally {
        setIsExporting(false);
      }
    });
  };

  const handleSaveToPhotos = () => {
    requirePremium(async () => {
      setIsExporting(true);
      try {
        await pngRef.current?.saveToPhotos();
      } finally {
        setIsExporting(false);
      }
    });
  };

  const showTitleInput = selectedFormat !== "share" && selectedFormat !== "code";
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const hasFixedButton = selectedFormat === "svg" || selectedFormat === "png";

  const modalHeights = {
    svg: 500,
    png: maintainAspectRatio ? 580 : 680,
    code: 560,
    share: 500,
    embroidery: isMobile ? 85 : 90,
  };
  const modalHeight = modalHeights[selectedFormat] || 420;

  const buttonLabels = {
    svg: isExporting ? "Downloading…" : "Download SVG",
    png: isExporting ? "Downloading…" : "Download PNG",
  };

  const buttonHandlers = {
    svg: handleExportSvg,
    png: handleExportPng,
  };

  return (
    <>
      <button
        type={buttonType}
        onClick={handleOpenModal}
        style={{
          fontWeight: 600,
          fontFamily: "Clash Grotesk",
          background: variant === "outlined"
            ? (isHovered ? "#F453C4" : "rgba(255,255,255,0.95)")
            : (isHovered ? "#c7429f" : "#F453C4"),
          transition: "all 0.15s ease",
          border: "1px solid #F453C4",
          color: variant === "outlined"
            ? (isHovered ? "#fff" : "#F453C4")
            : "#F8F8F8",
          padding: "6px 12px",
          borderRadius: "8px",
          cursor: "pointer",
          fontSize: "11px",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          backdropFilter: "blur(8px)",
          ...externalStyle,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        {...props}
      >
        {children}
      </button>

      <IonModal
        id="export-modal"
        isOpen={showModal}
        onDidDismiss={() => setShowModal(false)}
        style={{ 
          "--height": selectedFormat === "embroidery" 
            ? `${modalHeight}vh` 
            : `min(${modalHeight}px, ${isMobile ? "85vh" : "90vh"})`,
          "--max-height": isMobile ? "85vh" : "90vh",
          "--border-radius": "16px",
          "--width": "min(480px, 94vw)",
          "--max-width": "480px",
        }}
      >
        <style>{`
          #export-modal .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(82, 130, 255, 0.25) transparent;
          }
          #export-modal .custom-scrollbar::-webkit-scrollbar { width: 6px; }
          #export-modal .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          #export-modal .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(82, 130, 255, 0.25);
            border-radius: 3px;
          }
          #export-modal .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(82, 130, 255, 0.45);
          }
          #export-modal ion-content { --overflow: auto; }
          #export-modal ion-content::part(scroll) {
            scrollbar-width: thin;
            scrollbar-color: rgba(82, 130, 255, 0.25) transparent;
          }
          #export-modal ion-content::part(scroll)::-webkit-scrollbar { width: 6px; }
          #export-modal ion-content::part(scroll)::-webkit-scrollbar-track { background: transparent; }
          #export-modal ion-content::part(scroll)::-webkit-scrollbar-thumb {
            background: rgba(82, 130, 255, 0.25);
            border-radius: 3px;
          }
          @keyframes exportSpin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>

        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px 0 20px",
          background: "#fff",
          flexShrink: 0,
        }}>
          <span style={{
            fontSize: "18px",
            fontWeight: 700,
            fontFamily: "Clash Grotesk",
            color: "#1a1a1a",
          }}>
            Export
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                onClick={() => setSelectedFormat("share")}
                style={{
                  background: selectedFormat === "share" ? "#F453C4" : "#fdf0fa",
                  border: "1px solid #F453C4",
                  cursor: "pointer",
                  padding: "6px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  borderRadius: "20px",
                  color: selectedFormat === "share" ? "#fff" : "#F453C4",
                  fontSize: "13px",
                  fontWeight: 700,
                  fontFamily: "Clash Grotesk",
                  transition: "all 0.15s",
                  boxShadow: "0 1px 6px rgba(244,83,196,0.2)",
                }}
                onMouseEnter={(e) => {
                  if (selectedFormat !== "share") {
                    e.currentTarget.style.background = "#F453C4";
                    e.currentTarget.style.color = "#fff";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedFormat !== "share") {
                    e.currentTarget.style.background = "#fdf0fa";
                    e.currentTarget.style.color = "#F453C4";
                  }
                }}
              >
                <IonIcon icon={linkOutline} style={{ fontSize: "15px" }} />
                Share link
              </button>
            <button
              onClick={() => setShowModal(false)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "5px",
                display: "flex",
                alignItems: "center",
                borderRadius: "6px",
                color: "#888",
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#333"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#888"}
            >
              <IonIcon icon={closeOutline} style={{ fontSize: "22px" }} />
            </button>
          </div>
        </div>

        {/* Format tabs */}
        <>
          <div style={{
            display: "flex",
            gap: "4px",
            padding: "12px 20px 0 20px",
            background: "#fff",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            flexShrink: 0,
          }}>
            {FORMAT_TABS.map((tab) => {
              const isActive = selectedFormat === tab.key;
              const isShareTab = tab.highlight;
              return (
                <button
                  key={tab.key}
                  data-tutorial={tab.key === "svg" ? "export-formats" : undefined}
                  onClick={() => {
                    if (tab.key === "embroidery" && onEditorModeChange) {
                      setShowModal(false);
                      onEditorModeChange("stitch");
                      return;
                    }
                    setSelectedFormat(tab.key);
                  }}
                  style={{
                    flex: "0 0 auto",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "8px 14px",
                    border: "none",
                    borderRadius: "8px 8px 0 0",
                    background: isActive
                      ? (isShareTab ? "linear-gradient(135deg, #fff0fb 0%, #f8f0ff 100%)" : "#f8f0ff")
                      : "transparent",
                    color: isActive ? "#c7429f" : isShareTab ? "#F453C4" : "#777",
                    fontWeight: isActive || isShareTab ? 600 : 500,
                    fontSize: "13px",
                    fontFamily: "DM Sans, sans-serif",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    borderBottom: isActive ? "2px solid #F453C4" : "2px solid transparent",
                    whiteSpace: "nowrap",
                  }}
                >
                  <IonIcon icon={tab.icon} style={{ fontSize: "15px" }} />
                  {tab.label}
                </button>
              );
            })}
            {onEditorModeChange && !isSvgTrace && (
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  onEditorModeChange("stitch");
                }}
                style={{
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "8px 10px",
                  border: "none",
                  borderRadius: "8px 8px 0 0",
                  background: "transparent",
                  color: "#F453C4",
                  fontWeight: 700,
                  fontSize: "13px",
                  fontFamily: "DM Sans, sans-serif",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  borderBottom: "2px solid transparent",
                  whiteSpace: "nowrap",
                }}
              >
                <IonIcon icon={diamondOutline} style={{ fontSize: "15px" }} />
                Stitch
              </button>
            )}
          </div>

          {/* Divider */}
          <div style={{ height: "1px", background: "#f0f0f0", margin: "0 20px", flexShrink: 0 }} />
        </>

        {/* Scrollable content area */}
        <IonContent>
          <div style={{ padding: "16px 20px 20px 20px" }}>

            {/* Title input - shown for file-based exports */}
            {showTitleInput && (
              <div style={{ marginBottom: "16px" }}>
                <label style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#888",
                  marginBottom: "6px",
                  fontFamily: "DM Sans, sans-serif",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}>
                  File Name
                </label>
                <input
                  aria-label="File Name"
                  placeholder="File Name"
                  value={exportName}
                  onChange={(e) => setExportName(e.target.value)}
                  style={{
                    width: "100%",
                    background: "#f8f9fa",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    border: "1px solid #e9ecef",
                    fontSize: "14px",
                    fontFamily: "DM Sans, sans-serif",
                    color: "#1a1a1a",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = "#F453C4"}
                  onBlur={(e) => e.currentTarget.style.borderColor = "#e9ecef"}
                />
              </div>
            )}

            {/* === SVG Format === */}
            {selectedFormat === "svg" && (
              <SvgExportSettings 
                ref={svgRef}
                svgJoined={svgJoined}
                exportName={exportName}
                onExport={() => setShowModal(false)}
              />
            )}

            {/* === PNG Format === */}
            {selectedFormat === "png" && (
              <SVGtoPNGResizer
                ref={pngRef}
                svgJson={svgJoined}
                exportName={exportName}
                maintainAspectRatio={maintainAspectRatio}
                setMaintainAspectRatio={setMaintainAspectRatio}
              />
            )}

            {/* === Share === */}
            {selectedFormat === "share" && (
              <div>
                {/* Hero section */}
                <div style={{
                  textAlign: "center",
                  padding: "8px 0 20px 0",
                }}>
                  <div style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #F453C4 0%, #c7429f 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px auto",
                    boxShadow: "0 4px 16px rgba(244, 83, 196, 0.35)",
                  }}>
                    <IonIcon icon={linkOutline} style={{ fontSize: "26px", color: "#fff" }} />
                  </div>
                  <h3 style={{
                    margin: "0 0 6px 0",
                    fontSize: "17px",
                    fontWeight: 700,
                    fontFamily: "Clash Grotesk",
                    color: "#1a1a1a",
                  }}>
                    Share your design
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#6b7280",
                    lineHeight: 1.5,
                  }}>
                    {currentFile?.isPublic
                      ? "Your design is public — share the link below."
                      : "Get a link anyone can open, no account needed."}
                  </p>
                </div>

                {!publicLink && !isLoadingLink && (
                  <button
                    onClick={handleCreate}
                    disabled={isCreatingLink}
                    style={{
                      width: "100%",
                      padding: "15px",
                      background: isCreatingLink
                        ? "linear-gradient(135deg, #e0a0d0 0%, #c7a0bf 100%)"
                        : "linear-gradient(135deg, #F453C4 0%, #c7429f 100%)",
                      border: "none",
                      borderRadius: "10px",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "15px",
                      cursor: isCreatingLink ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      boxShadow: isCreatingLink ? "none" : "0 4px 16px rgba(244, 83, 196, 0.4)",
                      transition: "all 0.15s",
                    }}
                  >
                    <IonIcon icon={linkOutline} style={{ fontSize: "18px" }} />
                    {isCreatingLink ? "Creating link…" : "Create Share Link"}
                  </button>
                )}

                {isLoadingLink && (
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "24px 0",
                  }}>
                    <div style={{
                      width: "24px",
                      height: "24px",
                      border: "3px solid #f3f3f3",
                      borderTop: "3px solid #F453C4",
                      borderRadius: "50%",
                      animation: "exportSpin 1s linear infinite",
                      marginBottom: "12px"
                    }} />
                    <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>
                      Loading share link…
                    </p>
                  </div>
                )}

                {publicLink && !isLoadingLink && (
                  <div>
                    {/* Link row */}
                    <div style={{ display: "flex", gap: "0", marginBottom: "16px" }}>
                      <Input
                        style={{
                          fontWeight: 500,
                          border: "1px solid #e5e7eb",
                          borderRight: "none",
                          borderRadius: "8px 0 0 8px",
                          fontSize: "13px",
                          background: "#f9fafb",
                        }}
                        value={publicLink}
                        readOnly
                      />
                      <Tooltip title={linkCopied ? "Copied!" : "Copy link"} color="#000">
                        <Button
                          style={{
                            border: "1px solid #F453C4",
                            borderRadius: "0 8px 8px 0",
                            background: linkCopied ? "#22c55e" : "#F453C4",
                            color: "#fff",
                            minWidth: "44px",
                            height: "auto",
                            transition: "background 0.2s",
                          }}
                          icon={linkCopied ? <CheckOutlined /> : <CopyOutlined />}
                          onClick={handleCopyLink}
                        />
                      </Tooltip>
                    </div>

                    {/* Social share buttons */}
                    <p style={{ margin: "0 0 10px 0", fontSize: "12px", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Share on
                    </p>
                    <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                      <button
                        onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(publicLink)}&text=${encodeURIComponent("Check out my SVG design made with SvgTrace! 🎨")}`, "_blank")}
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          background: "#000",
                          border: "none",
                          borderRadius: "8px",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: "13px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          transition: "opacity 0.15s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                      >
                        𝕏 Post
                      </button>
                      <button
                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicLink)}`, "_blank")}
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          background: "#1877F2",
                          border: "none",
                          borderRadius: "8px",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: "13px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          transition: "opacity 0.15s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                      >
                        Facebook
                      </button>
                      <button
                        onClick={() => window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(publicLink)}&description=${encodeURIComponent("SVG design made with SvgTrace")}`, "_blank")}
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          background: "#E60023",
                          border: "none",
                          borderRadius: "8px",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: "13px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          transition: "opacity 0.15s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                      >
                        Pinterest
                      </button>
                    </div>

                    <p style={{ margin: 0, fontSize: "11px", color: "#d1d5db", textAlign: "center" }}>
                      Also visible in the community gallery at svgtrace.com
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* === Code === */}
            {selectedFormat === "code" && (
              <div>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}>
                  <span style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#888",
                    fontFamily: "DM Sans, sans-serif",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    SVG Code
                  </span>
                  <Button
                    icon={<CopyOutlined />}
                    size="small"
                    onClick={handleCopyCode}
                    style={{
                      border: "1px solid #e9ecef",
                      borderRadius: "6px",
                      fontSize: "12px",
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <div style={{
                  background: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  borderRadius: "10px",
                  padding: "14px",
                  maxHeight: "280px",
                  overflowY: "auto",
                  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                  fontSize: "11.5px",
                  lineHeight: "1.5",
                  color: "#374151",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(82, 130, 255, 0.25) transparent",
                }} className="custom-scrollbar">
                  {stringify(svgJoined)}
                </div>
              </div>
            )}

            {/* === Embroidery === */}
            {selectedFormat === "embroidery" && (
              <EmbroideryConverter 
                svgJoined={svgJoined}
                exportName={exportName}
                onBeforeExport={requirePremium}
              />
            )}
          </div>
        </IonContent>

        {/* Pinned download button — always visible for SVG/PNG */}
        {hasFixedButton && (
          <div style={{
            padding: "12px 20px 16px 20px",
            background: "#fff",
            borderTop: "1px solid #f0f0f0",
            flexShrink: 0,
          }}>
            {selectedFormat === "svg" && isNative() && (
              <div style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                marginBottom: "10px",
                padding: "8px 10px",
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: "8px",
              }}>
                <IonIcon icon={informationCircleOutline} style={{ fontSize: "15px", color: "#d97706", flexShrink: 0, marginTop: "1px" }} />
                <span style={{ fontSize: "11.5px", color: "#92400e", lineHeight: 1.4 }}>
                  Choose <strong>"Save to Files"</strong> to keep it as an SVG. Saving to Photos will convert it.
                </span>
              </div>
            )}
            <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={buttonHandlers[selectedFormat]}
              disabled={isExporting}
              style={{
                flex: 1,
                padding: "14px",
                background: isExporting
                  ? "linear-gradient(135deg, #e0a0d0 0%, #c7a0bf 100%)"
                  : "linear-gradient(135deg, #F453C4 0%, #c7429f 100%)",
                border: "none",
                borderRadius: "8px",
                color: "#fff",
                fontWeight: 600,
                fontSize: "15px",
                cursor: isExporting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                transition: "transform 0.15s, box-shadow 0.15s",
                opacity: isExporting ? 0.85 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isExporting) {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(244, 83, 196, 0.4)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {isExporting ? (
                <div style={{
                  width: "18px",
                  height: "18px",
                  border: "2.5px solid rgba(255,255,255,0.3)",
                  borderTop: "2.5px solid #fff",
                  borderRadius: "50%",
                  animation: "exportSpin 1s linear infinite",
                }} />
              ) : (
                <IonIcon icon={downloadOutline} style={{ fontSize: "18px" }} />
              )}
              {buttonLabels[selectedFormat]}
            </button>
            {selectedFormat === "png" && isNative() && (
              <button
                onClick={handleSaveToPhotos}
                disabled={isExporting}
                style={{
                  padding: "14px 18px",
                  background: isExporting
                    ? "linear-gradient(135deg, #86efac 0%, #6bc88a 100%)"
                    : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: isExporting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  whiteSpace: "nowrap",
                  opacity: isExporting ? 0.85 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isExporting) {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(34, 197, 94, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <IonIcon icon={imagesOutline} style={{ fontSize: "18px" }} />
                Save to Photos
              </button>
            )}
            </div>
          </div>
        )}
      </IonModal>

      {showIOSPaywall && (
        <IOSPaywall
          standalone
          action="export"
          onClose={() => setShowIOSPaywall(false)}
          onSuccess={() => setShowIOSPaywall(false)}
        />
      )}
    </>
  );
};

export default ExportFile;
